import role10Schema from '../../../../assets/forms/role-220000.form.json';
import { SSMT, SSMT_SCOPE } from '../../../constants/qnames';

/**
 * Shared business rules for Role 10 (Statement of Financial Position by Order
 * of Liquidity, role-220000). The role 10 component and the pure validator
 * both import from this file so the layout-flag derivation, the calculated-
 * field qname list, and the latent-bug guard helpers live in exactly ONE
 * place.
 *
 * ---- Schema variant handling --------------------------------------------
 *
 * Role 10 ships ONE form JSON (role-220000.form.json). Unlike role 9 there
 * is no MFRS/MPERS split — the liquidity layout uses the same structure for
 * every accounting standard. We still expose `pickRole10Schema` for surface
 * symmetry with the rest of the validator family so callers don't need to
 * know whether a given role has variants.
 *
 * ---- hasErrors derivation (parity target) -------------------------------
 *
 * The role 10 component publishes its sheet-level status EXACTLY ONCE per
 * mount, via `markSheetInitializedWithStatus('statementOfFinancialPositionLiquidity', false)`
 * (component L474, L484). There is no `evaluateSheetStatus()` method, no
 * post-init `updateSheetLevelStatus` call, and no valueChanges-driven
 * status push. The only ongoing state writes from the component are
 * `updateSheetData(..., true)` (debounced autosave) and `setValue(...)` on
 * the calculated total cells.
 *
 * Net effect: the editor's published `hasErrors` for this sheet is
 * deterministically `false`. The pure validator MUST mirror that to keep
 * the dev parity harness from throwing.
 *
 * However, the schema declares `required: true` on several rows
 * (CashAndCashEquivalentsLiquidity, TradeReceivablesLiquidity, ...) and
 * the component's `buildForm()` does wire `Validators.required` for them.
 * `form.invalid` therefore flips while the user has empty required cells —
 * but that flag never reaches `updateSheetLevelStatus`. This is a latent
 * bug (LB-014, see ledger) and matches role 8's published surface where
 * the parity-mirroring decision is the same.
 *
 * The validator carries the LB-001 fix-guard pattern (detect required from
 * BOTH `control.required === true` AND unconditional
 * `validators[{ name: 'required' }]`) for the day the editor is fixed to
 * actually publish row errors — that day, only the validator's
 * `hasErrors` derivation needs flipping; the schema scan is already in
 * place.
 *
 * ---- Cross-sheet posture ------------------------------------------------
 *
 * Role 10 is listed in `SheetDependencyService` as a DEPENDENT of
 * `scopeOfFiling` (Role 2) — Role 2 changes invalidate role 10's cached
 * status. Within role 10 itself the cross-sheet reads at
 * `checkConsolidatedStatus()` are:
 *
 *   • scopeOfFiling.DisclosureOfWhetherFinancialStatementsAreConsolidated
 *   • scopeOfFiling.DisclosureOfComparativeInformation
 *
 * Both drive column visibility (1 / 2 / 4 columns) only. They do NOT
 * influence the published hasErrors today (because the editor does not
 * publish row errors at all — see above). The validator still reads
 * `context.crossSheetData?.get('scopeOfFiling')` and
 * `context.crossSheetData?.get('filingPrerequisites')` defensively so the
 * orchestrator can pass an empty Map or omit keys without breaking parity,
 * and so a future cross-sheet equality rule can plug in here without a
 * second interface change.
 */

/** Filename of the role 10 schema (single variant). */
export const ROLE10_SCHEMA_FILE = 'role-220000.form.json';

/**
 * scopeOfFiling field whose value drives consolidation column visibility.
 * Mirrors the component's `checkConsolidatedStatus()` read.
 */
export const ROLE10_CONSOLIDATED_FIELD =
  SSMT_SCOPE.DisclosureOfWhetherFinancialStatementsAreConsolidated;
export const ROLE10_CONSOLIDATED_VALUE = 'Yes';

/**
 * scopeOfFiling field whose value drives previous-year column visibility.
 * Mirrors the component's `checkConsolidatedStatus()` read.
 */
export const ROLE10_COMPARATIVE_FIELD =
  SSMT_SCOPE.DisclosureOfComparativeInformation;
export const ROLE10_COMPARATIVE_PRESENT_VALUES: ReadonlyArray<string> = [
  'Comparative information presented',
  'Restated comparative information',
];

/**
 * Narrow shape of a role 10 schema control. The full schema carries far
 * more metadata (ContextPolicy, label, type, etc.) but the validator only
 * needs qname, required, validators, and ui.calculated/readonly/dualColumn —
 * keeping this type tight makes it resilient to unrelated schema additions.
 */
export interface Role10SchemaControl {
  qname: string;
  type?: string;
  required?: boolean;
  validators?: Array<{
    name?: string;
    value?: number;
    conditionalOn?: unknown;
  }>;
  ui?: {
    control?: string;
    calculated?: boolean;
    readonly?: boolean;
    dualColumn?: boolean;
  };
}

export interface Role10Schema {
  role: string;
  sheetId: string;
  label: string;
  controls: Role10SchemaControl[];
}

/**
 * Resolve the role 10 schema. Single-variant today — accepts (and ignores)
 * an accounting-standard argument so the call shape matches the multi-variant
 * sheets (`pickRole9Schema`, `pickRole13Schema`, ...) and a future variant
 * split would be a one-line change here, not a callsite refactor.
 */
export function pickRole10Schema(
  _accountingStandard?: string | null
): Role10Schema {
  return role10Schema as unknown as Role10Schema;
}

/**
 * LB-001 fix-guard: a control is statically required when EITHER
 * `required === true` OR the validators array contains an unconditional
 * `{ name: 'required' }` entry. Mirrors the role-6 post-fix check so any
 * future schema edit (e.g. someone removes the top-level `required: true`
 * but keeps the validators entry) is picked up automatically.
 */
export function isRole10StaticallyRequired(
  control: Role10SchemaControl
): boolean {
  if (control.required === true) return true;
  return Array.isArray(control.validators)
    ? control.validators.some(
        (validator) => validator?.name === 'required' && validator?.conditionalOn === undefined
      )
    : false;
}

/** Empty-value check matching the component's null/undefined/'' guards. */
export function isRole10Empty(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

/**
 * Calculated row qnames that the component fills via `calculateTotals()`
 * and registers via `registerCalculatedKeys()`. Centralised here so both
 * the component and the validator agree on which rows are derived rather
 * than user-entered. Order matches the calculation block in the component
 * for readability.
 */
export const ROLE10_CALCULATED_ROW_QNAMES: ReadonlyArray<string> = [
  SSMT.TotalAssetsLiquidity,
  SSMT.TotalLiabilitiesLiquidity,
  SSMT.TotalEquityLiquidity,
  SSMT.TotalEquityAndLiabilitiesLiquidity,
];

/**
 * Component (`buildForm()`) registers four column-suffix flavours per
 * dualColumn row depending on the consolidation × comparative flags. The
 * validator needs the same expansion so a future "row required" check can
 * iterate over the live set of column keys rather than a single qname.
 *
 * Mirrors the component's branch order at L255-299.
 */
export type Role10Suffix =
  | 'GroupCurrent'
  | 'GroupPrevious'
  | 'CompanyCurrent'
  | 'CompanyPrevious'
  | 'Current'
  | 'Previous';

export interface Role10LayoutFlags {
  isConsolidated: boolean;
  hasPreviousYear: boolean;
}

/**
 * Resolve the visible column suffixes for the current filing. Mirrors the
 * component's `calculateTotals()` suffix derivation (L380-389).
 *
 * Defaults: when the cross-sheet bundle is missing or the relevant scope
 * fields are unset, we treat the filing as `isConsolidated=false,
 * hasPreviousYear=false` — the SAME default the component applies when
 * `scopeOfFiling` has not been populated yet (see `checkConsolidatedStatus()`
 * — both flags fall through to `false`). This keeps the validator's column
 * footprint aligned with what the editor would render in the same state.
 */
export function resolveRole10VisibleSuffixes(
  scopeOfFiling: Record<string, unknown> | undefined
): ReadonlyArray<Role10Suffix> {
  const flags = resolveRole10LayoutFlags(scopeOfFiling);

  if (flags.isConsolidated && flags.hasPreviousYear) {
    return ['GroupCurrent', 'GroupPrevious', 'CompanyCurrent', 'CompanyPrevious'];
  }
  if (flags.isConsolidated && !flags.hasPreviousYear) {
    return ['GroupCurrent', 'CompanyCurrent'];
  }
  if (!flags.isConsolidated && flags.hasPreviousYear) {
    return ['Current', 'Previous'];
  }
  return ['Current'];
}

/**
 * Derive the layout flags from a scopeOfFiling slice. Exposed as a separate
 * helper so the component can consume the SAME derivation at
 * `checkConsolidatedStatus()` — keeping the two paths byte-for-byte
 * comparable.
 */
export function resolveRole10LayoutFlags(
  scopeOfFiling: Record<string, unknown> | undefined
): Role10LayoutFlags {
  const isConsolidated =
    scopeOfFiling?.[ROLE10_CONSOLIDATED_FIELD] === ROLE10_CONSOLIDATED_VALUE;

  const comparativeInfo = scopeOfFiling?.[ROLE10_COMPARATIVE_FIELD];
  const hasPreviousYear =
    typeof comparativeInfo === 'string' &&
    ROLE10_COMPARATIVE_PRESENT_VALUES.includes(comparativeInfo);

  return { isConsolidated, hasPreviousYear };
}

/**
 * Build the suffixed key the editor stores its dual-column values under
 * (e.g. `"ssmt:CashAndCashEquivalentsLiquidityGroupCurrent"`). Note this
 * differs from role 8/9's `[Dimension][Period]` bracket notation — role 10's
 * component concatenates the suffix directly onto the qname (see
 * buildForm() L257-298). Centralised here so any future validator that
 * needs to read role 10 cells uses the same key shape.
 */
export function buildRole10ColumnKey(
  qname: string,
  suffix: Role10Suffix
): string {
  return `${qname}${suffix}`;
}
